import { useRef, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useLanguage } from "@/contexts/LanguageContext";
import { dirFor } from "@/lib/i18n/languages";
import { getProtocolCopy } from "@/lib/protocol-copy-all-languages";
import type { Severity } from "@/lib/protocol";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subtitle: string;
  onSave: (args: {
    description: string;
    severity: Severity;
    file?: File | null;
  }) => Promise<void>;
};

export function ReportDefectSheet({
  open,
  onOpenChange,
  subtitle,
  onSave,
}: Props) {
  const { language } = useLanguage();
  const t = getProtocolCopy(language);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<Severity>("medium");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setDescription("");
    setSeverity("medium");
    setSaving(false);
  };

  const handleFile = (f: File | null) => {
    setFile(f);
    if (f) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const handleSave = async () => {
    if (!description.trim()) {
      toast.error(t.describeShort);
      return;
    }
    setSaving(true);
    try {
      await onSave({ description: description.trim(), severity, file });
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message ?? t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const severityLabel = (s: Severity) =>
    s === "low"
      ? t.severityLow
      : s === "medium"
        ? t.severityMedium
        : t.severityHigh;

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <SheetContent
        side="bottom"
        dir={dirFor(language)}
        className="rounded-t-3xl border-0 px-5 pb-[max(env(safe-area-inset-bottom),20px)] pt-3"
      >
        <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-neutral-200" />
        <SheetHeader className="items-start pb-4 text-left">
          <SheetTitle className="flex items-center gap-3 text-lg">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-500">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <span>
              <div>{t.reportDamageTitle}</div>
              <div className="text-xs font-normal text-neutral-500">
                {subtitle}
              </div>
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {t.photoOfDamage}
            </div>
            {preview ? (
              <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-neutral-100">
                <img
                  src={preview}
                  alt=""
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleFile(null)}
                  className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-neutral-900/80 text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="flex aspect-video w-full items-center justify-center rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50 text-sm text-neutral-500"
              >
                {t.capturePhoto}
              </button>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {t.description}
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.descriptionPlaceholder}
              rows={3}
              className="w-full resize-none rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm leading-snug outline-none placeholder:text-neutral-400 focus:border-emerald-400 focus:bg-white"
            />
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {t.severity}
            </div>
            <div className="flex gap-2">
              {(["low", "medium", "high"] as Severity[]).map((s) => {
                const selected = severity === s;
                const isLow = s === "low";
                const isHigh = s === "high";
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSeverity(s)}
                    className={[
                      "flex-1 rounded-full px-3 py-2 text-sm font-medium transition",
                      selected
                        ? "bg-neutral-900 text-white"
                        : isLow
                          ? "bg-emerald-100 text-emerald-700"
                          : isHigh
                            ? "bg-rose-100 text-rose-600"
                            : "bg-amber-100 text-amber-700",
                    ].join(" ")}
                  >
                    {severityLabel(s)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-12 rounded-full border border-neutral-200 text-sm font-semibold text-neutral-700"
            >
              {t.cancel}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex h-12 items-center justify-center rounded-full bg-emerald-500 text-sm font-semibold text-white shadow-md shadow-emerald-500/30 disabled:bg-neutral-300"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t.saveDamage
              )}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
